import { AodRun, AodRunStatusEnum } from '@app/modules/audit/modules/aod/aod.types';
import { toDateTimeStr } from '@mi-front/core';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { LDateTime } from '../../../../../../../components/date/LDateTime';
import { MutedAlert } from '../../../../../../../components/MutedAlert';
import { UserDisplay, UsersChipList } from '../../../../../../../components/users';
import { AodRunStatusWithProgress } from '../../../components/AodRunStatusWithProgress';
import { InfoM } from '../../aod-rule-view/components/InfoM';
import { AodRunCompletionCommentInfo } from './AodRunCompletionCommentInfo';
import { AodRunCompletionReasonInfo } from './AodRunCompletionReasonInfo';

interface AodRunStatusInfoProps {
  run: AodRun;
}

export function AodRunStatusInfo({ run }: AodRunStatusInfoProps) {
  const { formatMessage } = useIntl();
  const statusData = run.statusData;
  const completedStatusData =
    run.status === AodRunStatusEnum.COMPLETED_WITH_EXPORT
      ? statusData?.completedWithExport
      : run.status === AodRunStatusEnum.COMPLETED_WITHOUT_EXPORT
        ? statusData?.completedWithoutExport
        : undefined;
  const deficiencyNotifications = statusData?.deficiencyNotifications;
  const userAccepted = statusData?.userAccepted;
  const userReprocess = statusData?.userReprocess;

  // Format efficiency as percentage
  const formatEfficiency = (efficiency: number) => {
    return `${Math.floor(efficiency)}%`;
  };

  // Check if minEfficiency is a number (not false)
  // minEfficiency can be false or a number (0-100), but is typed as object
  const getMinEfficiency = (minEfficiency?: object | number): number | undefined => {
    if (!minEfficiency) {
      return;
    }
    // Check if it's actually a number (despite being typed as object)
    const value = minEfficiency as unknown;
    if (value === false || typeof value === 'boolean') {
      return;
    }

    return value as number;
  };

  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={2}>
            <InfoM
              label={formatMessage({
                id: 'audit.aod.runs.field.status',
                defaultMessage: 'Estado',
              })}
            >
              <AodRunStatusWithProgress run={run} size="small" />
            </InfoM>
          </Grid>

          {completedStatusData && (
            <>
              <AodRunCompletionReasonInfo run={run} />
              <Grid item>
                <InfoM
                  label={formatMessage({
                    id: 'audit.aod.runs.field.efficiency',
                    defaultMessage: 'Eficiencia',
                  })}
                >
                  {formatEfficiency(completedStatusData.efficiency)}
                </InfoM>
              </Grid>
              {getMinEfficiency(completedStatusData.minEfficiency) && (
                <Grid item>
                  <InfoM
                    label={formatMessage({
                      id: 'audit.aod.runs.field.minEfficiency',
                      defaultMessage: 'Eficiencia mínima requerida',
                    })}
                  >
                    {formatEfficiency(getMinEfficiency(completedStatusData.minEfficiency)!)}
                  </InfoM>
                </Grid>
              )}
              <AodRunCompletionCommentInfo run={run} />
            </>
          )}
        </Grid>
      </Grid>

      {deficiencyNotifications && (
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item>
              <InfoM
                label={formatMessage({
                  id: 'audit.aod.runs.field.deficiencyNotificationsSent',
                  defaultMessage: 'Notificaciones de deficiencia enviadas',
                })}
              >
                <LDateTime value={toDateTimeStr(deficiencyNotifications.sentAt)} showRelative />
              </InfoM>
            </Grid>
            {deficiencyNotifications.userIds.length > 0 && (
              <Grid item>
                <InfoM
                  label={formatMessage({
                    id: 'audit.aod.runs.field.deficiencyNotificationsUsers',
                    defaultMessage: 'Usuarios notificados',
                  })}
                >
                  <UsersChipList userIds={deficiencyNotifications.userIds} />
                </InfoM>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}

      {userReprocess && (
        <Grid item xs={12}>
          <Alert severity="warning">
            {formatMessage({
              id: 'audit.aod.runs.userReprocess.intro',
              defaultMessage: 'Reprocesamiento manual solicitado por el usuario',
            })}{' '}
            {userReprocess?.userId ? (
              <strong>
                <UserDisplay id={userReprocess.userId} />
              </strong>
            ) : (
              formatMessage({
                id: 'audit.aod.runs.userReprocess.unknownUser',
                defaultMessage: 'usuario desconocido',
              })
            )}{' '}
            {formatMessage({
              id: 'audit.aod.runs.userReprocess.onDate',
              defaultMessage: 'el',
            })}{' '}
            <LDateTime value={toDateTimeStr(userReprocess.startedAt)} />
          </Alert>
        </Grid>
      )}

      {userAccepted && (
        <Grid item xs={12}>
          <MutedAlert icon={<HowToRegIcon />}>
            <Box>
              {formatMessage({
                id: 'audit.aod.runs.userAccepted.intro',
                defaultMessage: 'Ejecución aceptada manualmente por el usuario',
              })}{' '}
              {userAccepted.userId && (
                <strong>
                  <UserDisplay id={userAccepted.userId} />
                </strong>
              )}{' '}
              {formatMessage({
                id: 'audit.aod.runs.userReprocess.onDate',
                defaultMessage: 'el',
              })}{' '}
              <LDateTime value={toDateTimeStr(userAccepted.acceptedAt)} />
            </Box>
            {userAccepted.reason && (
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage({
                    id: 'audit.aod.runs.userAccepted.reason',
                    defaultMessage: 'Razón:',
                  })}{' '}
                  {userAccepted.reason}
                </Typography>
              </Box>
            )}
          </MutedAlert>
        </Grid>
      )}
    </>
  );
}
